import React, { useState, useEffect } from 'react';
import { RefreshCw, Settings as SettingsIcon, Database, ShoppingCart, DollarSign, Key, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://store.comparehomeupgrades.com/api';

export function EbaySettings({ storeId }) {
  const [syncing, setSyncing]         = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [storeInfo, setStoreInfo]     = useState(null);

  // Auth token state
  const [authToken, setAuthToken]         = useState('');
  const [showToken, setShowToken]         = useState(false);
  const [savingToken, setSavingToken]     = useState(false);

  useEffect(() => {
    if (storeId) fetchStoreInfo();
  }, [storeId]);

  const fetchStoreInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API_BASE_URL}/ebay/stores/${storeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setStoreInfo(data.store);
    } catch {}
  };

  // ── Save new eBay Auth Token to DB ────────────────────────────────────────
  const handleSaveAuthToken = async () => {
    if (!authToken.trim()) {
      toast.error('Please paste your eBay Auth Token first');
      return;
    }
    try {
      setSavingToken(true);
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API_BASE_URL}/ebay/stores/${storeId}/auth-token`, {
        method:  'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ ebay_auth_token: authToken.trim() })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('eBay Auth Token updated for all stores! You can now sync inventory.');
        setAuthToken('');
      } else {
        toast.error(`Failed to save token: ${data.error}`);
      }
    } catch {
      toast.error('Failed to save token');
    } finally {
      setSavingToken(false);
    }
  };

  // ── Full Sync ─────────────────────────────────────────────────────────────
  const handleFullSync = async () => {
    try {
      setSyncing(true);
      toast.info('Starting full sync...');
      const token = localStorage.getItem('token');

      toast.info('Syncing products from eBay...');
      const productsRes  = await fetch(`${API_BASE_URL}/ebay/${storeId}/products/sync`, {
        method:  'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const productsData = await productsRes.json();
      if (productsData.success) {
        toast.success(`Synced ${productsData.synced} products with latest costs`);
      } else {
        toast.error(`Product sync failed: ${productsData.error}`);
      }

      toast.info('Syncing orders from eBay...');
      const ordersRes  = await fetch(`${API_BASE_URL}/ebay/${storeId}/orders/sync`, {
        method:  'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({})
      });
      const ordersData = await ordersRes.json();
      if (ordersData.success) {
        toast.success(`Synced ${ordersData.synced} order items`);
      } else {
        toast.error(`Order sync failed: ${ordersData.error}`);
      }

      toast.success('Full sync completed!');
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // ── Backfill costs ────────────────────────────────────────────────────────
  const handleBackfillCosts = async () => {
    try {
      setBackfilling(true);
      toast.info('Backfilling historical costs...');
      const token = localStorage.getItem('token');
      const res   = await fetch(`${API_BASE_URL}/ebay/${storeId}/orders/backfill-costs`, {
        method:  'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Updated ${data.updated} historical orders with correct costs!`);
      } else {
        toast.error('Backfill failed');
      }
    } catch {
      toast.error('Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">eBay Settings</h1>

      {/* Store Connection */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <ShoppingCart className="text-blue-600 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">eBay Store Connection</h2>
        </div>
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
          <p className="text-blue-800 font-medium">✓ Connected to eBay</p>
          {storeInfo && (
            <div className="mt-2 space-y-1">
              <p className="text-blue-700 text-sm">Store: <strong>{storeInfo.store_name}</strong></p>
              <p className="text-blue-700 text-sm">Marketplace: {storeInfo.marketplace_id || 'EBAY_GB'}</p>
              {storeInfo.store_description && (
                <p className="text-blue-600 text-sm">{storeInfo.store_description}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── eBay Auth Token (Trading API) ── */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <Key className="text-orange-500 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">eBay Auth Token (Trading API)</h2>
        </div>

        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded-r-lg mb-4">
          <p className="text-orange-800 text-sm font-medium">
            ⚠️ This token is required for inventory sync (GetSellerList). It expires every ~18 months and must be manually renewed.
          </p>
        </div>

        <p className="text-gray-600 text-sm mb-2">
          To get a new token:
        </p>
        <ol className="text-gray-600 text-sm mb-4 space-y-1 list-decimal list-inside">
          <li>Go to <a href="https://developer.ebay.com/my/auth" target="_blank" rel="noreferrer" className="text-blue-600 underline">developer.ebay.com/my/auth</a></li>
          <li>Select your app → Production → <strong>Auth'n Auth Token</strong></li>
          <li>Click <strong>Generate Token</strong> and log in with your eBay seller account</li>
          <li>Copy the token and paste it below</li>
        </ol>

        <div className="relative mb-3">
          <textarea
            value={authToken}
            onChange={(e) => setAuthToken(e.target.value)}
            placeholder="Paste your new eBay Auth Token here (starts with v^1.1#i^1#...)"
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm font-mono resize-none pr-12"
            style={{ filter: showToken ? 'none' : 'blur(3px)' }}
          />
          <button
            onClick={() => setShowToken(s => !s)}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            title={showToken ? 'Hide token' : 'Show token'}
          >
            {showToken ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <button
          onClick={handleSaveAuthToken}
          disabled={savingToken || !authToken.trim()}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg disabled:opacity-50"
        >
          <Key className="mr-2" size={18} />
          {savingToken ? 'Saving...' : 'Save Auth Token'}
        </button>
      </div>

      {/* Full Sync */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <RefreshCw className="text-blue-600 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Synchronization</h2>
        </div>
        <p className="text-gray-600 mb-2">
          Syncs all products with latest costs from eBay, then syncs new orders.
        </p>
        <p className="text-gray-500 text-sm mb-4">
          ℹ️ First run fetches all orders. After that, only new orders are fetched.
        </p>
        <button
          onClick={handleFullSync}
          disabled={syncing || backfilling}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg disabled:opacity-50"
        >
          <RefreshCw className={`mr-2 ${syncing ? 'animate-spin' : ''}`} size={20} />
          {syncing ? 'Syncing...' : 'Run Full Sync'}
        </button>
        <p className="text-gray-500 text-sm mt-3">
          ℹ️ Automatic sync runs every 15 minutes in the background
        </p>
      </div>

      {/* Backfill Historical Costs */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <DollarSign className="text-blue-600 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Backfill Historical Costs</h2>
        </div>
        <p className="text-gray-600 mb-2">
          Updates cost price and net profit for all existing eBay orders using the latest product costs.
        </p>
        <p className="text-gray-500 text-sm mb-4">
          ℹ️ Run this once after your first full sync to fix profit data on all historical orders.
        </p>
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg mb-4">
          <p className="text-yellow-800 text-sm font-medium">
            ⚠️ Make sure to run "Run Full Sync" first so product costs are up to date before backfilling.
          </p>
        </div>
        <button
          onClick={handleBackfillCosts}
          disabled={syncing || backfilling}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all shadow-lg disabled:opacity-50"
        >
          <DollarSign className={`mr-2 ${backfilling ? 'animate-spin' : ''}`} size={20} />
          {backfilling ? 'Backfilling...' : 'Backfill Historical Costs'}
        </button>
      </div>

      {/* Fee Structure */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <SettingsIcon className="text-purple-600 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Fee Structure</h2>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">Final Value Fee</span>
            <span className="font-semibold text-gray-800">12.8%</span>
          </div>
          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">Fixed Fee per Order</span>
            <span className="font-semibold text-gray-800">£0.30</span>
          </div>
          <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
            <span className="text-gray-700 font-medium">Total Fee Formula</span>
            <span className="font-semibold text-blue-600">(Price × 12.8%) + £0.30</span>
          </div>
        </div>
        <p className="text-gray-500 text-sm mt-4">
          ℹ️ Fees may vary by category. Above rates are for standard items on eBay UK.
        </p>
      </div>

      {/* Database */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center mb-4">
          <Database className="text-purple-600 mr-3" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Database</h2>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">Database Status</span>
            <span className="font-semibold text-green-600">Connected</span>
          </div>
          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">Store ID</span>
            <span className="font-semibold text-gray-800">{storeId}</span>
          </div>
          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">Last Sync</span>
            <span className="font-semibold text-gray-800">{new Date().toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EbaySettings;